// @flow
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import withTheme from 'hoc/withTheme'
import Button, { ButtonGroup } from 'components/Button'
import supportedThemes from './themes/__supportedThemes'

import transDomain from './translations/index.translations'

import { Store } from 'Store'
import * as Form from 'components/Form'
import withForm, { WithFormProps } from 'components/Form/Helpers/FormHOC'

import styles from './styles.module.scss'

type Props = WithFormProps | {
    values: {
        currentVehicleMileage: ?number,
        currentVehicleCondition: ?string,
        currentVehicleFinanceMethod: ?string,
        currentVehicleLeasePayment: ?string,
        currentVehicleLeasePaymentsLeft: ?string,
        currentVehicleFinancePayment: ?string,
        currentVehicleFinanceAmountOwing: ?string,
        storeLocationId: ?string,
        newVehicleMake: ?string,
        newVehicleModel: ?string,
        hotButtons: ?string,
        salesperson: ?string,
        timeline: ?string,
    }
}

const HOT_BUTTONS = ['safety', 'performance', 'dependability', 'appearance', 'comfort', 'economy']

function Step2 (props: Props) {
    const { theme } = props
    const { t } = useTranslation(transDomain)
    const { state } = React.useContext(Store)

    const makes = state.campaign.models.map(model => model.make).filter((value, index, self) => self.indexOf(value) === index)

    if (makes.length === 1 && props.values.newVehicleMake !== makes[0]) {
        props.handleFieldChange('newVehicleMake', makes[0])
    }

    if (state.campaign.locations.length === 1 && !props.values.storeLocationId) {
        props.handleFieldChange('storeLocationId', state.campaign.locations[0].id)
    }

    const salesReps = React.useMemo((): { [string]: string } => {
        const selectedStoreLocation = state.campaign.locations.find(location => location.id === props.values.storeLocationId)
        if (!selectedStoreLocation) return {}

        return {
            ...selectedStoreLocation.salesReps.reduce((salesReps, rep) => ({ ...salesReps, [rep.name]: rep.name }), {}),
            'none': t('field.salesperson.options.none.label')
        }
    }, [props.values.storeLocationId, state.campaign.locations, t])

    const hotButtons = React.useMemo(() => (props.values.hotButtons || []).map(i => i.toLowerCase()), [ props.values.hotButtons ])

    const handleHotButtonSelect = value => {
        const values = hotButtons
        let a
        a = values.indexOf(value) !== -1
            ? values.filter(item => item !== value) // deselect
            : [...values, value].splice(-2) // keep the most recent 2
        props.handleFieldChange('hotButtons', a)
    }

    const firstName = `${state.lead.fields.firstName || ''}`

    return (
        <div style={{
            '--font-family': theme.fontFamily
        }}>
            <div className={styles.heading}>
                <h2 style={{ color: theme.titleColor }}>
                    {t('heading', { name: firstName })}
                </h2>

                <div className={styles.description} style={{ color: theme.descriptionColor }}>
                    {t('description')}
                </div>
            </div>

            <ButtonGroup className={styles.buttonGroup}>
                <Form.Row>
                    <Form.Field noBorder label={t('field.hotButtons.label')}>
                        <Form.RowColumns>
                            <Form.RowColumn className={styles.rowWrapper} size={1}>
                                {HOT_BUTTONS.map(choice => (
                                    <Button
                                        key={choice}
                                        onClick={() => handleHotButtonSelect(choice)}
                                        style={hotButtons.indexOf(choice) !== -1 ? theme.activeButton : theme.inactiveButton}>
                                        {t(`field.hotButtons.choice.${choice}`)}
                                    </Button>
                                ))}
                                <input
                                    tabIndex={-1}
                                    type='text'
                                    value={hotButtons.length === 2 ? `${hotButtons.length}` : ''}
                                    required={true}
                                    onChange={() => false}
                                    style={{
                                        opacity: 0,
                                        position: 'absolute',
                                        left: 0,
                                        right: 0,
                                        top: 0,
                                        bottom: '10%',
                                        zIndex: 0
                                    }}
                                />
                            </Form.RowColumn>
                        </Form.RowColumns>
                    </Form.Field>
                </Form.Row>
            </ButtonGroup>

            <Form.Row>
                <Form.RowColumns>
                    <Form.RowColumn className={styles.dropsWrapper}>
                        <Form.Field label={t('field.timeline.label')}>
                            <Form.Dropdown
                                // error={props.errors.timeline || null}
                                placeholder={t('field.timeline.placeholder')}
                                options={['immediately', 'lessThanAWeek', 'aFewWeeks', 'aFewMonths']}
                                optionFormat={'field.timeline.options.%name%'}
                                trans={t}
                                onChange={value => props.handleFieldChange('timeline', value)}
                                defaultValue={props.values.timeline}
                            />
                        </Form.Field>
                    </Form.RowColumn>

                    <Form.RowColumn>
                        <Form.Field label={t('field.salesperson.label')}>
                            <Form.Dropdown
                                // error={props.errors.salesperson || null}
                                placeholder={t('field.salesperson.placeholder')}
                                options={salesReps}
                                onChange={value => props.handleFieldChange('salesperson', value)}
                                defaultValue={props.values.salesperson}
                            />
                        </Form.Field>
                    </Form.RowColumn>
                </Form.RowColumns>
            </Form.Row>

            <div className={styles.submitContainer}>
                <Form.Submit
                    isLoading={props.isLoading}
                    label={t('submit.label')}
                    style={{
                        background: theme.background,
                        color: theme.color
                    }}
                />
            </div>
        </div>
    )
}

export default withForm()(withTheme(supportedThemes)(Step2))
